'use client';
import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function PrivacyPolicyPage() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef?.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <main className="bg-canopy min-h-screen" ref={sectionRef}>
      <Header />

      <div className="pt-32 pb-20 px-6 md:px-10 blueprint-grid">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-px bg-chartreuse opacity-60" />
              <span className="annotation">KEBIJAKAN PRIVASI</span>
            </div>
            <h1 className="font-mono font-bold text-4xl md:text-5xl text-text-primary mb-4 leading-tight">
              Kebijakan<br />
              <span className="text-chartreuse">Privasi.</span>
            </h1>
            <p className="text-text-muted text-sm">
              Terakhir diperbarui: 2025 · Agro Teknologi Nusantara
            </p>
          </div>

          <div className="space-y-10">
            {/* Komitmen */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">Komitmen Privasi</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Agro Teknologi Nusantara menghargai privasi setiap pengguna website
                dan layanan kami. Kebijakan Privasi ini menjelaskan bagaimana kami mengumpulkan,
                menggunakan, dan melindungi informasi pribadi Anda.
              </p>
            </section>

            {/* Informasi yang dikumpulkan */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">Informasi yang Dikumpulkan</h2>
              <div className="space-y-3 text-text-muted text-sm leading-relaxed">
                <p>Kami dapat mengumpulkan informasi berikut:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Nama lengkap dan informasi kontak</li>
                  <li>Alamat email</li>
                  <li>Nomor telepon</li>
                  <li>Alamat pengiriman dan penagihan</li>
                  <li>Informasi pembayaran</li>
                  <li>Data penggunaan situs web dan layanan IoT</li>
                  <li>Data dari perangkat IoT yang terintegrasi dengan sistem kami</li>
                </ul>
              </div>
            </section>

            {/* Penggunaan informasi */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">Penggunaan Informasi</h2>
              <p className="text-text-muted text-sm leading-relaxed mb-3">
                Informasi yang kami kumpulkan digunakan untuk:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-muted text-sm leading-relaxed pl-4">
                <li>Memproses pesanan dan permintaan layanan</li>
                <li>Memberikan layanan pelanggan dan dukungan teknis</li>
                <li>Mengirim informasi produk dan layanan (dengan persetujuan)</li>
                <li>Materi pemasaran (dengan persetujuan eksplisit)</li>
                <li>Memperbaiki pengalaman pengguna di website</li>
                <li>Mengembangkan dan meningkatkan layanan IoT dan platform SaaS</li>
                <li>Analisis data untuk forecasting dan penelitian pertanian</li>
              </ul>
            </section>

            {/* Keamanan data */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">Keamanan Data</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Kami menerapkan prosedur keamanan yang sesuai untuk melindungi informasi pribadi
                Anda dari akses tidak sah, pengubahan, pengungkapan, atau penghancuran. Ini
                mencakup enkripsi data, firewall, dan kontrol akses yang ketat pada sistem kami.
              </p>
            </section>

            {/* Pengungkapan pihak ketiga */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">Pengungkapan kepada Pihak Ketiga</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Kami tidak menjual atau menyewakan data pribadi Anda untuk tujuan komersial tanpa
                izin Anda. Kami dapat membagikan informasi dengan mitra tepercaya untuk
                mengoperasikan bisnis, sepanjang mereka setuju menjaga kerahasiaan data tersebut.
              </p>
            </section>

            {/* Hak akses */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">Hak Akses & Pengelolaan Data</h2>
              <p className="text-text-muted text-sm leading-relaxed mb-3">
                Anda berhak untuk:
              </p>
              <ul className="list-disc list-inside space-y-2 text-text-muted text-sm leading-relaxed pl-4">
                <li>Mengakses data pribadi yang kami miliki tentang Anda</li>
                <li>Memperbarui atau memperbaiki informasi yang tidak akurat</li>
                <li>Meminta penghapusan data pribadi Anda</li>
                <li>Menarik persetujuan untuk pemasaran kapan saja</li>
              </ul>
              <p className="text-text-muted text-sm leading-relaxed mt-4">
                Untuk hak Anda, hubungi kami di{' '}
                <a href="mailto:ptagroteknologinusantara@gmail.com" className="text-chartreuse hover:underline">
                  ptagroteknologinusantara@gmail.com
                </a>
                .
              </p>
            </section>

            {/* Cookie */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">Cookie</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Website kami mungkin menggunakan cookie untuk meningkatkan pengalaman pengguna.
                Anda dapat mengatur browser untuk memblokir cookie jika diinginkan.
              </p>
            </section>
          </div>

          {/* CTA */}
          <div className="reveal mt-16 border border-chartreuse/20 rounded-sm p-8 bg-pond/30 text-center">
            <p className="text-text-muted text-sm mb-4">
              Ada pertanyaan tentang kebijakan privasi kami?
            </p>
            <a
              href="/quote"
              className="btn-chartreuse px-6 py-3 rounded-sm text-xs uppercase tracking-widest inline-block"
            >
              Hubungi Kami →
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
